import {createDropdown, createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const taskFields = {
    AIVectorStore: ['documentsExpression'],
    AIVectorSearch: ['queryExpression', 'topK', 'similarityThreshold'],
    AIVectorDelete: ['idsExpression']
};

const expressionLabels = {
    documentsExpression: 'vectorStoreDocumentsExpression',
    queryExpression: 'queryExpression',
    idsExpression: 'idsExpression',
    topK: 'vectorTopK',
    similarityThreshold: 'vectorSimilarityThreshold'
};

const expressionHelp = {
    queryExpression: 'vectorQueryExpressionHelp',
    idsExpression: 'vectorIdsExpressionHelp',
    topK: 'vectorTopKHelp',
    similarityThreshold: 'vectorSimilarityThresholdHelp'
};

const vectorTaskValue = task => {
    const fields = taskFields[task?.type] || [];
    return {
        vectorStore: typeof task?.vectorStore === 'string' ? task.vectorStore : '',
        ...Object.fromEntries(fields.filter(field => Object.hasOwn(task, field)).map(field => [field, task[field]]))
    };
};

const createAIVectorStoreTaskForm = ({task = {}, resources = {}, taskErrors = {}, editable = true,
                                        mxResources, onChange = () => {}}) => {
    let current = vectorTaskValue(task);
    let documentsInput = null;
    let destroyed = false;
    let destroying = false;
    const element = document.createElement('div');
    element.id = 'aiVectorStoreEditor';
    element.className = 'ui form';

    const emit = patch => {
        current = {...current, ...patch};
        onChange(cloneValue(current));
    };

    const references = Object.keys(resources?.vectorStores || {}).map(name => ({
        value: `#/resources/vectorStores/${name.replaceAll('~', '~0').replaceAll('/', '~1')}`,
        name
    }));
    const selectedReference = references.some(reference => reference.value === current.vectorStore)
        ? current.vectorStore : '';
    const resourceField = createDropdown({
        label: mxResources.get('vectorStoreResource'), id: 'vectorStoreResource', name: 'vectorStore',
        className: 'required', value: selectedReference, error: taskErrors?.fields?.vectorStore,
        help: mxResources.get('vectorStoreResourceHelp'),
        options: [{value: '', name: mxResources.get('vectorStoreResourceSelect')}, ...references]
    });
    resourceField.querySelector('select').addEventListener('change', event => emit({vectorStore: event.target.value}));
    element.appendChild(resourceField);

    (taskFields[task?.type] || []).forEach(fieldName => {
        const numeric = fieldName === 'topK' || fieldName === 'similarityThreshold';
        const field = createInputField({
            label: mxResources.get(expressionLabels[fieldName]), id: fieldName, name: fieldName,
            type: numeric ? 'number' : 'text', className: numeric ? '' : 'required',
            min: numeric ? (fieldName === 'topK' ? 1 : 0) : undefined,
            step: fieldName === 'similarityThreshold' ? 'any' : undefined,
            value: current[fieldName] ?? '', error: taskErrors?.fields?.[fieldName],
            placeHolder: fieldName === 'documentsExpression' ? mxResources.get('expressionPlaceholder') : undefined,
            help: fieldName === 'documentsExpression' ? undefined : mxResources.get(expressionHelp[fieldName])
        });
        const input = field.querySelector('input');
        if (fieldName === 'documentsExpression') {
            documentsInput = input;
        }
        input.addEventListener('change', event => emit({
            [fieldName]: numeric && event.target.value !== '' ? Number(event.target.value) : event.target.value
        }));
        element.appendChild(field);
    });

    if (!editable) {
        disableFormControls(element);
    }

    return {
        element,
        getTask: () => cloneValue(current),
        appendVectorStoreInputHelp(help) {
            if (!destroyed && task?.type === 'AIVectorStore' && help && !element.querySelector('details')) {
                element.insertAdjacentHTML('beforeend', help);
            }
        },
        sync() {
            if (!destroyed && documentsInput && !documentsInput.disabled && !documentsInput.readOnly &&
                (current.documentsExpression ?? '') !== documentsInput.value) {
                emit({documentsExpression: documentsInput.value});
            }
        },
        destroy() {
            if (destroyed || destroying) {
                return;
            }
            destroying = true;
            this.sync();
            destroyed = true;
        }
    };
};

export {createAIVectorStoreTaskForm, vectorTaskValue};
